export type AutosaveEntityStatus =
  | "idle"
  | "dirty"
  | "saving"
  | "saved"
  | "conflict"
  | "blocked"
  | "failed";

export type AutosaveCommitOutcome =
  | { status: "saved"; newToken?: string | null }
  | { status: "conflict" }
  | { status: "blocked" }
  | { status: "rejected"; dropFields: ReadonlyArray<string> }
  | { status: "failed" };

export type AutosaveFields = Record<string, unknown>;

export type AutosaveEvent =
  | { type: "dirty-changed"; anyDirty: boolean }
  | {
      type: "entity";
      entityId: string;
      status: AutosaveEntityStatus;
      savedAt: number | null;
    };

export interface AutosaveEngineOptions<F extends AutosaveFields> {
  /** The one write path; classifies its own failures into outcomes. */
  commit: (
    entityId: string,
    fields: Partial<F>,
    token: string | null
  ) => Promise<AutosaveCommitOutcome>;
  /** Never flush on the timer — a half-typed number waits for blur/Done. */
  explicitFields?: ReadonlyArray<string>;
  debounceMs?: number;
  /** Backoff per retry; exhausted → failed with the buffer kept. */
  retryDelaysMs?: ReadonlyArray<number>;
  token?: string | null;
  onEvent?: (event: AutosaveEvent) => void;
}

interface EntityState<F extends AutosaveFields> {
  staged: Partial<F>;
  explicitStaged: Partial<F>;
  timer: ReturnType<typeof setTimeout> | null;
  status: AutosaveEntityStatus;
  savedAt: number | null;
  queuedExplicit: boolean | null;
  dropEpoch: number;
}

// the engine is generic and buffer keys become dynamic property writes —
// prototype-polluting keys never enter a buffer
const UNSAFE_FIELDS = new Set(["__proto__", "constructor", "prototype"]);

const DEFAULT_DEBOUNCE_MS = 750;
const DEFAULT_RETRY_DELAYS_MS = [1000, 4000] as const;

const wait = (ms: number) =>
  new Promise<void>((resolve) => {
    setTimeout(resolve, ms);
  });

export class AutosaveEngine<F extends AutosaveFields = AutosaveFields> {
  private readonly options: Required<
    Pick<AutosaveEngineOptions<F>, "commit" | "debounceMs" | "retryDelaysMs">
  > & { explicitFields: Set<string> };

  private listeners = new Set<(event: AutosaveEvent) => void>();

  private entities = new Map<string, EntityState<F>>();

  private token: string | null;

  private tail: Promise<void> = Promise.resolve();

  private stopped = false;

  private lastAnyDirty = false;

  constructor(options: AutosaveEngineOptions<F>) {
    this.options = {
      commit: options.commit,
      debounceMs: options.debounceMs ?? DEFAULT_DEBOUNCE_MS,
      retryDelaysMs: options.retryDelaysMs ?? DEFAULT_RETRY_DELAYS_MS,
      explicitFields: new Set(options.explicitFields ?? []),
    };
    if (options.onEvent) this.listeners.add(options.onEvent);
    this.token = options.token ?? null;
  }

  subscribe(listener: (event: AutosaveEvent) => void): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  private emit(event: AutosaveEvent) {
    this.listeners.forEach((listener) => listener(event));
  }

  private entity(entityId: string): EntityState<F> {
    let state = this.entities.get(entityId);
    if (!state) {
      state = {
        staged: {},
        explicitStaged: {},
        timer: null,
        status: "idle",
        savedAt: null,
        queuedExplicit: null,
        dropEpoch: 0,
      };
      this.entities.set(entityId, state);
    }
    return state;
  }

  private emitEntity(entityId: string, state: EntityState<F>) {
    this.emit({
      type: "entity",
      entityId,
      status: state.status,
      savedAt: state.savedAt,
    });
  }

  private emitDirty() {
    const anyDirty = this.anyDirty();
    if (anyDirty !== this.lastAnyDirty) {
      this.lastAnyDirty = anyDirty;
      this.emit({ type: "dirty-changed", anyDirty });
    }
  }

  private setStatus(
    entityId: string,
    state: EntityState<F>,
    status: AutosaveEntityStatus
  ) {
    if (state.status === status) return;
    state.status = status;
    this.emitEntity(entityId, state);
  }

  getToken(): string | null {
    return this.token;
  }

  setToken(token: string | null) {
    this.token = token;
  }

  /** Adopts only a strictly newer token — cache reads may lag a flush. */
  adoptTokenIfNewer(token: string | null | undefined) {
    if (!token) return;
    if (!this.token || new Date(token) > new Date(this.token)) {
      this.token = token;
    }
  }

  isStopped(): boolean {
    return this.stopped;
  }

  stop() {
    this.stopped = true;
    this.entities.forEach((state) => {
      if (state.timer !== null) {
        clearTimeout(state.timer);
        state.timer = null;
      }
    });
  }

  resume() {
    this.stopped = false;
  }

  isDirty(entityId: string): boolean {
    const state = this.entities.get(entityId);
    if (!state) return false;
    return (
      Object.keys(state.staged).length > 0 ||
      Object.keys(state.explicitStaged).length > 0
    );
  }

  anyDirty(): boolean {
    return Array.from(this.entities.keys()).some((id) => this.isDirty(id));
  }

  statusOf(entityId: string): { status: AutosaveEntityStatus; savedAt: number | null } {
    const state = this.entities.get(entityId);
    return {
      status: state?.status ?? "idle",
      savedAt: state?.savedAt ?? null,
    };
  }

  pendingFields(entityId: string): string[] {
    const state = this.entities.get(entityId);
    if (!state) return [];
    return [
      ...Object.keys(state.staged),
      ...Object.keys(state.explicitStaged),
    ];
  }

  stage(entityId: string, fields: Partial<F>) {
    if (this.stopped) return;
    const state = this.entity(entityId);
    let armed = false;
    Object.entries(fields).forEach(([field, value]) => {
      if (UNSAFE_FIELDS.has(field)) return;
      if (this.options.explicitFields.has(field)) {
        (state.explicitStaged as AutosaveFields)[field] = value;
      } else {
        (state.staged as AutosaveFields)[field] = value;
        armed = true;
      }
    });
    if (state.status !== "saving") {
      this.setStatus(entityId, state, "dirty");
    }
    if (armed) {
      if (state.timer !== null) clearTimeout(state.timer);
      state.timer = setTimeout(() => {
        state.timer = null;
        this.enqueue(entityId, false);
      }, this.options.debounceMs);
    }
    this.emitDirty();
  }

  drop(entityId: string, fields?: ReadonlyArray<string>) {
    const state = this.entities.get(entityId);
    if (!state) return;
    if (fields) {
      fields.forEach((field) => {
        delete (state.staged as AutosaveFields)[field];
        delete (state.explicitStaged as AutosaveFields)[field];
      });
    } else {
      state.staged = {};
      state.explicitStaged = {};
    }
    state.dropEpoch += 1;
    if (!this.isDirty(entityId)) {
      if (state.timer !== null) {
        clearTimeout(state.timer);
        state.timer = null;
      }
      if (state.status === "dirty" || state.status === "failed" || state.status === "conflict") {
        this.setStatus(entityId, state, "idle");
      }
    }
    this.emitDirty();
  }

  dropAll() {
    Array.from(this.entities.keys()).forEach((entityId) => this.drop(entityId));
  }

  /** Resolves once this entity's queued work has actually run — a tail that
   *  later enqueues reassign is re-awaited until the entity is quiet. */
  async flush(entityId: string): Promise<void> {
    const state = this.entities.get(entityId);
    if (state?.timer !== null && state?.timer !== undefined) {
      clearTimeout(state.timer);
      state.timer = null;
    }
    this.enqueue(entityId, true);
    for (;;) {
      await this.settled();
      if (this.entities.get(entityId)?.queuedExplicit == null) return;
    }
  }

  flushAll(): Promise<void> {
    Array.from(this.entities.keys()).forEach((entityId) => {
      const state = this.entities.get(entityId)!;
      if (state.timer !== null) {
        clearTimeout(state.timer);
        state.timer = null;
      }
      this.enqueue(entityId, true);
    });
    return this.settled();
  }

  retry(entityId: string): Promise<void> {
    return this.flush(entityId);
  }

  /** Flushes until quiet; parked entities keep their buffers and never
   *  hold the drain open. */
  async drain(): Promise<void> {
    for (;;) {
      const before = this.dirtySignature();
      await this.flushAll();
      const after = this.dirtySignature();
      if (after === "" || after === before) return;
    }
  }

  private dirtySignature(): string {
    return Array.from(this.entities.entries())
      .filter(([entityId]) => this.isDirty(entityId))
      .map(
        ([entityId, state]) =>
          `${entityId}:${Object.keys(state.staged).sort().join(",")}|${Object.keys(
            state.explicitStaged
          )
            .sort()
            .join(",")}`
      )
      .sort()
      .join(";");
  }

  private settled(): Promise<void> {
    return this.tail;
  }

  private enqueue(entityId: string, explicit: boolean) {
    const state = this.entity(entityId);
    if (state.queuedExplicit !== null) {
      state.queuedExplicit = state.queuedExplicit || explicit;
      return;
    }
    state.queuedExplicit = explicit;
    this.tail = this.tail.then(() => this.runFlush(entityId));
  }

  private async runFlush(entityId: string): Promise<void> {
    const state = this.entity(entityId);
    const explicit = state.queuedExplicit ?? false;
    state.queuedExplicit = null;
    if (this.stopped) return;

    const snapshot: Partial<F> = { ...state.staged };
    if (explicit) {
      Object.assign(snapshot, state.explicitStaged);
      state.explicitStaged = {};
    }
    state.staged = {};
    if (Object.keys(snapshot).length === 0) return;
    const epoch = state.dropEpoch;

    this.setStatus(entityId, state, "saving");

    let outcome: AutosaveCommitOutcome = { status: "failed" };
    for (let attempt = 0; ; attempt += 1) {
      try {
        outcome = await this.options.commit(entityId, snapshot, this.token);
      } catch {
        outcome = { status: "failed" };
      }
      if (outcome.status !== "failed") break;
      if (this.stopped || attempt >= this.options.retryDelaysMs.length) break;
      await wait(this.options.retryDelaysMs[attempt]);
    }

    if (outcome.status === "saved") {
      if (outcome.newToken) this.token = outcome.newToken;
      state.savedAt = Date.now();
      this.setStatus(entityId, state, this.isDirty(entityId) ? "dirty" : "saved");
      if (Object.keys(state.staged).length > 0 && state.timer === null) {
        state.timer = setTimeout(() => {
          state.timer = null;
          this.enqueue(entityId, false);
        }, this.options.debounceMs);
      }
      this.emitDirty();
      return;
    }

    const retired = new Set(
      outcome.status === "rejected" ? outcome.dropFields : []
    );
    if (epoch === state.dropEpoch) {
      Object.entries(snapshot).forEach(([field, value]) => {
        if (retired.has(field)) return;
        const target = this.options.explicitFields.has(field)
          ? state.explicitStaged
          : state.staged;
        if (!(field in target)) {
          (target as AutosaveFields)[field] = value;
        }
      });
    }
    if (outcome.status === "rejected") {
      retired.forEach((field) => {
        delete (state.staged as AutosaveFields)[field];
        delete (state.explicitStaged as AutosaveFields)[field];
      });
      if (this.isDirty(entityId)) {
        this.setStatus(entityId, state, "dirty");
        if (Object.keys(state.staged).length > 0 && state.timer === null) {
          state.timer = setTimeout(() => {
            state.timer = null;
            this.enqueue(entityId, false);
          }, this.options.debounceMs);
        }
      } else {
        if (state.timer !== null) {
          clearTimeout(state.timer);
          state.timer = null;
        }
        this.setStatus(entityId, state, "idle");
      }
      this.emitDirty();
      return;
    }
    this.setStatus(entityId, state, outcome.status);
    if (outcome.status === "blocked") {
      this.stop();
    }
    this.emitDirty();
  }
}

export const createAutosaveEngine = <F extends AutosaveFields = AutosaveFields>(
  options: AutosaveEngineOptions<F>
) => new AutosaveEngine<F>(options);
